import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, ArrowUpRight, BookOpen } from "lucide-react";
import HeaderV2 from "./HeaderV2";
import FooterV2 from "./FooterV2";
import V2PageHero from "./sections/V2PageHero";
import CTAV2 from "./sections/CTAV2";
import InlineCTA from "./sections/InlineCTA";
import useSmoothScroll from "../../hooks/useSmoothScroll";
import { listPosts } from "../../lib/api";
import { CATEGORIES, categoryLabel } from "../../lib/categories";
import { cn } from "../../lib/utils";

function fmt(d) {
  if (!d) return "";
  return new Date(d).toLocaleDateString("en-GB", {
    month: "long",
    year: "numeric",
  });
}

const FILTERS = [{ value: "all", label: "All" }, ...CATEGORIES];

export default function BlogV2() {
  useSmoothScroll(true);
  const [posts, setPosts] = useState(null);
  const [active, setActive] = useState("all");

  useEffect(() => {
    listPosts({ category: active })
      .then(setPosts)
      .catch(() => setPosts([]));
  }, [active]);

  return (
    <div data-testid="v2-blog-page" className="bg-white">
      <HeaderV2 />
      <main>
        <V2PageHero
          eyebrow="Resource Center"
          title="Founders' library —"
          highlight="UAE business insights."
          copy="Practical guides on UAE incorporation, corporate tax, VAT, accounting and immigration — written by our senior advisors."
          image="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=2400&q=80"
        />

        <section className="bg-white py-10 md:py-14">
          <div className="container-x">
            {/* Category filters */}
            <div className="flex flex-wrap gap-2 mb-8">
              {FILTERS.map((f) => (
                <button
                  key={f.value}
                  data-testid={`blog-filter-${f.value}`}
                  onClick={() => setActive(f.value)}
                  className={cn(
                    "px-4 py-2 rounded-full text-xs font-heading uppercase tracking-widest border transition-colors",
                    active === f.value
                      ? "bg-[#001A4D] text-white border-[#001A4D]"
                      : "bg-white text-slate-600 border-slate-300 hover:border-[#3366FF] hover:text-[#3366FF]"
                  )}
                >
                  {f.label}
                </button>
              ))}
            </div>

            {posts === null ? (
              <p className="text-slate-500 text-sm">Loading…</p>
            ) : posts.length === 0 ? (
              <div className="border border-dashed border-slate-300 rounded-sm p-16 text-center">
                <BookOpen className="w-8 h-8 text-slate-300 mx-auto" />
                <p className="mt-4 text-slate-500">
                  {active === "all"
                    ? "Articles coming soon. Check back shortly."
                    : `No posts in "${categoryLabel(active)}" yet.`}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {posts.map((p, i) => (
                  <motion.article
                    key={p.id}
                    data-testid={`blog-post-${i}`}
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{
                      duration: 0.55,
                      delay: i * 0.06,
                      ease: [0.16, 1, 0.3, 1],
                    }}
                    className="group rounded-sm overflow-hidden border border-slate-200 bg-white hover:shadow-2xl hover:border-[#3366FF]/40 transition-all duration-500"
                  >
                    <Link to={`/blog/${p.slug}`} className="block">
                      {p.cover_image && (
                        <div className="relative aspect-[16/10] overflow-hidden">
                          <img
                            src={p.cover_image}
                            alt={p.title}
                            className="w-full h-full object-cover transition-transform duration-[1400ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-110"
                            loading="lazy"
                          />
                        </div>
                      )}
                      <div className="p-6">
                        <div className="flex items-center gap-2 text-xs text-slate-500 font-heading uppercase tracking-wider">
                          <Calendar className="w-3.5 h-3.5" />
                          {fmt(p.published_at || p.created_at)} ·{" "}
                          {categoryLabel(p.category)}
                        </div>
                        <h3 className="mt-4 font-heading font-semibold text-[#0F172A] tracking-tight text-lg leading-snug">
                          {p.title}
                        </h3>
                        {p.excerpt && (
                          <p className="mt-2 text-sm text-slate-600 leading-relaxed line-clamp-3">
                            {p.excerpt}
                          </p>
                        )}
                        <div className="mt-5 inline-flex items-center gap-1.5 text-sm font-heading font-semibold text-[#3366FF]">
                          Read more
                          <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </div>
                      </div>
                    </Link>
                  </motion.article>
                ))}
              </div>
            )}
          </div>
        </section>

        <InlineCTA
          eyebrow="Want a topic covered?"
          title="Tell us what UAE-business question is on your mind — we'll publish it next."
          buttonLabel="Suggest a topic"
        />

        <CTAV2 />
      </main>
      <FooterV2 />
    </div>
  );
}
